import { useState, useEffect } from 'react';
import { useApiKey } from '../../contexts/ApiKeyContext';
import './ApiKeyModal.css';

/**
 * API Key Modal Component
 * 
 * This component displays a modal dialog that prompts the user to enter an API key.
 * It will be shown when the page is first loaded if no API key is found.
 */
const ApiKeyModal = ({ isOpen, onClose, onSuccess, onSkip }) => {
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [error, setError] = useState('');
  const { setApiKey } = useApiKey();
  
  // Handle key input change
  const handleApiKeyChange = (e) => {
    setApiKeyInput(e.target.value);
    if (error) setError('');
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const key = apiKeyInput.trim();
    if (!key) {
      setError('Please enter a valid API key');
      return;
    }
    
    const success = setApiKey(key);
    if (success) {
      if (onSuccess) onSuccess(key);
      if (onClose) onClose();
    } else {
      setError('Failed to store API key. Please try again.');
    }
  };
  
  // Handle skip button click
  const handleSkip = () => {
    if (onSkip) onSkip();
    if (onClose) onClose();
  };
  
  // Handle Escape key press to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleSkip();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);
  
  // Auto-focus the input field when modal opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const input = document.getElementById('api-key-input');
        if (input) input.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <div className="api-key-modal-overlay">
      <div className="api-key-modal">
        <h2>Enter API Key</h2>
        <p>
          To use the AI-powered diagram generation features, please enter your API key. 
          If you don't have an API key, you can still use the diagram preview functionality.
        </p>
        
        <form onSubmit={handleSubmit}>
          <input
            id="api-key-input"
            type="password"
            placeholder="Enter your API key"
            value={apiKeyInput}
            onChange={handleApiKeyChange}
          />
          
          {error && <div className="error">{error}</div>}
          
          <div className="buttons">
            <button 
              type="button" 
              className="secondary"
              onClick={handleSkip}
            >
              Skip
            </button>
            <button 
              type="submit" 
              className="primary"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApiKeyModal; 